import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
	COMPOSER_ADD_REGION_MIN_SCROLL_HEIGHT,
	COMPOSER_ADD_REGION_VIEWPORT_GAP,
	COMPOSER_SURFACE_VERTICAL_BORDER_PX,
	ComposerTab,
} from './composerConstants.ts';
import { useMeasuredHeight } from './useMeasuredHeight.ts';

interface UseComposerAddRegionLayoutOptions {
	isOpen: boolean;
	isMobile: boolean;
	expandsDown: boolean;
	activeTab: ComposerTab;
	isExpanded?: boolean;
	belowControlsVersion?: string;
	primaryTaskIsRecurring?: boolean;
}

export const useComposerAddRegionLayout = (options: UseComposerAddRegionLayoutOptions) => {
	const {
		isOpen,
		isMobile,
		expandsDown,
		activeTab,
		isExpanded = false,
		belowControlsVersion = '',
		primaryTaskIsRecurring = false,
	} = options;
	const composerFrameRef = useRef<HTMLDivElement>(null);
	const belowControlsRef = useRef<HTMLDivElement>(null);
	const belowControlsScrollerRef = useRef<HTMLDivElement>(null);
	const bottomRowRef = useRef<HTMLDivElement>(null);
	const collapsedBottomRowHeightRef = useRef(0);
	const previousPrimaryTaskRecurringRef = useRef(primaryTaskIsRecurring);
	const childHeightCacheRef = useRef(new Map<string, number>());
	const accountedRemovingChildIdsRef = useRef(new Set<string>());
	const belowControlsHeightRef = useRef(0);
	const availableHeightFrameRef = useRef<number | null>(null);
	const { ref: addHeaderRef, height: addHeaderHeight } = useMeasuredHeight();
	const { ref: addFormRef, height: addFormHeight } = useMeasuredHeight();
	const [availableComposerHeight, setAvailableComposerHeight] = useState<number | null>(null);
	const [bottomRowHeight, setBottomRowHeight] = useState(0);
	const [belowControlsHeight, setBelowControlsHeight] = useState(0);

	const updateAvailableComposerHeight = useCallback(() => {
		const frame = composerFrameRef.current;
		if (!frame || isMobile) return;

		const frameRect = frame.getBoundingClientRect();
		// A contained composer is clipped by its page layout, whose top can sit below a persistent
		// app header. Budget against that real boundary instead of letting an expanded composer grow
		// into the hidden space between the panel and the viewport edge.
		const pageBoundary = frame.closest<HTMLElement>('[data-page-scroll-layout]');
		const pageBoundaryRect = pageBoundary?.getBoundingClientRect();
		const availableTop = Math.max(0, pageBoundaryRect?.top ?? 0);
		const availableBottom = Math.min(window.innerHeight, pageBoundaryRect?.bottom ?? window.innerHeight);
		const nextAvailableHeight = expandsDown
			? availableBottom - frameRect.top - COMPOSER_ADD_REGION_VIEWPORT_GAP
			: frameRect.bottom - availableTop - COMPOSER_ADD_REGION_VIEWPORT_GAP;
		const clampedHeight = Math.max(0, nextAvailableHeight);
		setAvailableComposerHeight((currentHeight) =>
			currentHeight === clampedHeight ? currentHeight : clampedHeight,
		);
	}, [expandsDown, isMobile]);

	const scheduleAvailableComposerHeightUpdate = useCallback(() => {
		if (availableHeightFrameRef.current !== null) return;

		availableHeightFrameRef.current = window.requestAnimationFrame(() => {
			availableHeightFrameRef.current = null;
			updateAvailableComposerHeight();
		});
	}, [updateAvailableComposerHeight]);

	useLayoutEffect(() => {
		const region = belowControlsRef.current;
		if (!isOpen || isMobile || !region || !belowControlsVersion) {
			belowControlsHeightRef.current = 0;
			childHeightCacheRef.current.clear();
			accountedRemovingChildIdsRef.current.clear();
			setBelowControlsHeight(0);
			return;
		}

		const removingChildren = Array.from(
			region.querySelectorAll<HTMLElement>('[data-composer-child-removing="true"]'),
		);
		if (removingChildren.length > 0) {
			// Child heights were cached while stable. Avoid a synchronous scrollHeight read on the
			// removal click—the browser can begin the exit transition without a forced layout pass.
			const exitingHeight = removingChildren.reduce((height, child) => {
				const childId = child.dataset.composerChildId ?? '';
				if (accountedRemovingChildIdsRef.current.has(childId)) return height;
				accountedRemovingChildIdsRef.current.add(childId);
				return height + (childHeightCacheRef.current.get(childId) ?? 0);
			}, 0);
			const nextHeight = Math.max(0, belowControlsHeightRef.current - exitingHeight);
			belowControlsHeightRef.current = nextHeight;
			setBelowControlsHeight(nextHeight);
			return;
		}

		const nextHeight = region.scrollHeight;
		belowControlsHeightRef.current = nextHeight;
		setBelowControlsHeight(nextHeight);
		childHeightCacheRef.current.clear();
		accountedRemovingChildIdsRef.current.clear();
		region
			.querySelectorAll<HTMLElement>('[data-composer-child-id]')
			.forEach((child) =>
				childHeightCacheRef.current.set(child.dataset.composerChildId ?? '', child.scrollHeight),
			);
	}, [activeTab, belowControlsVersion, isMobile, isOpen]);

	useLayoutEffect(() => {
		if (!isOpen || isMobile) {
			setAvailableComposerHeight(null);
			return;
		}

		updateAvailableComposerHeight();
		window.addEventListener('resize', scheduleAvailableComposerHeightUpdate);
		window.addEventListener('scroll', scheduleAvailableComposerHeightUpdate, true);
		window.visualViewport?.addEventListener('resize', scheduleAvailableComposerHeightUpdate);

		return () => {
			window.removeEventListener('resize', scheduleAvailableComposerHeightUpdate);
			window.removeEventListener('scroll', scheduleAvailableComposerHeightUpdate, true);
			window.visualViewport?.removeEventListener('resize', scheduleAvailableComposerHeightUpdate);
			if (availableHeightFrameRef.current !== null) {
				window.cancelAnimationFrame(availableHeightFrameRef.current);
				availableHeightFrameRef.current = null;
			}
		};
	}, [activeTab, isOpen, isMobile, scheduleAvailableComposerHeightUpdate, updateAvailableComposerHeight]);

	useLayoutEffect(() => {
		const row = bottomRowRef.current;
		if (!row || isMobile) return;

		const wasRecurring = previousPrimaryTaskRecurringRef.current;
		const renderedHeight = row.getBoundingClientRect().height;
		if (!primaryTaskIsRecurring) {
			// When closing, the rendered height still starts at the open value. Reuse the stable
			// closed baseline captured before opening instead of observing every transition frame.
			if (!wasRecurring || collapsedBottomRowHeightRef.current === 0) {
				collapsedBottomRowHeightRef.current = renderedHeight;
			}
			setBottomRowHeight(collapsedBottomRowHeightRef.current || renderedHeight);
		} else {
			const frequencyContent = row.querySelector<HTMLElement>('[data-composer-frequency-content]');
			const frequencyHeight = frequencyContent?.scrollHeight ?? 0;
			const collapsedHeight = collapsedBottomRowHeightRef.current || renderedHeight;
			setBottomRowHeight(collapsedHeight + frequencyHeight);
		}
		previousPrimaryTaskRecurringRef.current = primaryTaskIsRecurring;
	}, [activeTab, isMobile, isOpen, primaryTaskIsRecurring]);

	const maxBelowControlsHeight =
		availableComposerHeight !== null
			? Math.max(
					0,
					availableComposerHeight -
						bottomRowHeight -
						addHeaderHeight -
						COMPOSER_ADD_REGION_MIN_SCROLL_HEIGHT -
						COMPOSER_SURFACE_VERTICAL_BORDER_PX,
				)
			: null;
	const visibleBelowControlsHeight =
		maxBelowControlsHeight !== null ? Math.min(belowControlsHeight, maxBelowControlsHeight) : belowControlsHeight;
	const maxAddHeight =
		availableComposerHeight !== null
			? Math.max(
					0,
					availableComposerHeight -
						bottomRowHeight -
						visibleBelowControlsHeight -
						COMPOSER_SURFACE_VERTICAL_BORDER_PX,
				)
			: null;

	const addContentHeight = addHeaderHeight + addFormHeight;
	const compactAddHeight =
		isOpen && maxAddHeight !== null ? Math.min(addContentHeight, maxAddHeight) : addContentHeight;
	const visibleAddHeight =
		isOpen && maxAddHeight !== null ? (isExpanded ? maxAddHeight : compactAddHeight) : addContentHeight;
	const maxAddFormHeight = maxAddHeight !== null ? Math.max(0, maxAddHeight - addHeaderHeight) : null;
	const isAddBodyScrollable = maxAddFormHeight !== null && addFormHeight > maxAddFormHeight;

	useLayoutEffect(() => {
		const scroller = belowControlsScrollerRef.current;
		if (!isOpen || isMobile || !scroller) return;

		const updateScrollbarWidth = () => {
			const scrollbarWidth = Math.max(0, scroller.offsetWidth - scroller.clientWidth);
			scroller.style.setProperty('--composer-scrollbar-width', `${scrollbarWidth}px`);
		};
		updateScrollbarWidth();
		const observer = new ResizeObserver(updateScrollbarWidth);
		observer.observe(scroller);
		return () => observer.disconnect();
	}, [belowControlsHeight, belowControlsVersion, isMobile, isOpen, maxBelowControlsHeight]);

	return {
		composerFrameRef,
		bottomRowRef,
		belowControlsRef,
		belowControlsScrollerRef,
		addHeaderRef,
		addFormRef,
		maxBelowControlsHeight,
		visibleAddHeight,
		maxAddFormHeight,
		isAddBodyScrollable,
	};
};
